import * as XLSX from 'xlsx';
import type { SubmissionRow, SubmissionsResponse } from '../types/submissions';

const FORM_TYPE_LABELS: Record<string, string> = {
  collateral: 'Marketing Collateral',
  'trade-show': 'Trade Show / Orders',
  'quick-note': 'Quick Note',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function exportSubmissionsToExcel(
  data: SubmissionsResponse,
  filename?: string
): void {
  const wb = XLSX.utils.book_new();

  // Sheet 1: All Submissions
  const submissionsData = data.rows.map((row: SubmissionRow) => ({
    'Date Submitted': formatDate(row.timestamp),
    'Name': row.name,
    'Email': row.email,
    'Department': row.department,
    'Request Type': FORM_TYPE_LABELS[row.formType] || row.formType,
    'Request Title': row.requestTitle,
    'Asana Task ID': row.asanaTaskId,
    'Asana Link': row.asanaTaskId
      ? `https://app.asana.com/0/0/${row.asanaTaskId}`
      : '',
  }));

  const ws1 = XLSX.utils.json_to_sheet(submissionsData);
  ws1['!cols'] = [
    { wch: 20 }, { wch: 22 }, { wch: 32 }, { wch: 16 },
    { wch: 24 }, { wch: 36 }, { wch: 18 }, { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(wb, ws1, 'Submissions');

  // Sheet 2: By Department
  const deptData = Object.entries(data.summary.byDepartment)
    .sort((a, b) => b[1] - a[1])
    .map(([dept, count]) => ({ Department: dept, Submissions: count }));
  const ws2 = XLSX.utils.json_to_sheet(deptData);
  ws2['!cols'] = [{ wch: 20 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'By Department');

  // Sheet 3: By Person
  const personData = Object.entries(data.summary.byPerson)
    .sort((a, b) => b[1] - a[1])
    .map(([person, count]) => ({ Person: person, Submissions: count }));
  const ws3 = XLSX.utils.json_to_sheet(personData);
  ws3['!cols'] = [{ wch: 40 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws3, 'By Person');

  // Sheet 4: By Request Type
  const typeData = Object.entries(data.summary.byFormType)
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({
      'Request Type': FORM_TYPE_LABELS[type] || type,
      Submissions: count,
    }));
  const ws4 = XLSX.utils.json_to_sheet(typeData);
  ws4['!cols'] = [{ wch: 26 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws4, 'By Type');

  const defaultFilename = `marketing-submissions-${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename || defaultFilename);
}
